/**
 * Capture the browser install prompt as early as possible.
 * `beforeinstallprompt` often fires before React mounts — if we only
 * listen inside a component, the event is missed and tap does nothing.
 */

let deferredPrompt = null;
const listeners = new Set();

function notify() {
  listeners.forEach((fn) => {
    try {
      fn(deferredPrompt);
    } catch (_) {
      /* ignore */
    }
  });
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
    window.__gbxDeferredInstallPrompt = e;
    notify();
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    window.__gbxDeferredInstallPrompt = null;
    notify();
  });
}

export function getDeferredInstallPrompt() {
  return deferredPrompt || (typeof window !== 'undefined' ? window.__gbxDeferredInstallPrompt : null) || null;
}

export function subscribeInstallPrompt(listener) {
  listeners.add(listener);
  listener(getDeferredInstallPrompt());
  return () => listeners.delete(listener);
}

export async function promptPwaInstall() {
  const promptEvent = getDeferredInstallPrompt();
  if (!promptEvent) return { outcome: 'unavailable' };

  promptEvent.prompt();
  try {
    const choice = await promptEvent.userChoice;
    deferredPrompt = null;
    window.__gbxDeferredInstallPrompt = null;
    notify();
    return { outcome: choice?.outcome || 'dismissed' };
  } catch (_) {
    return { outcome: 'error' };
  }
}

export function isPwaStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

export function isIosDevice() {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent || '';
  if (/iphone|ipod/i.test(ua)) return true;
  if (/ipad/i.test(ua)) return true;
  // iPadOS 13+ may report as Mac with touch
  if (/Macintosh/i.test(ua) && (window.navigator.maxTouchPoints || 0) > 1) return true;
  return false;
}

export function isAndroidDevice() {
  if (typeof window === 'undefined') return false;
  return /android/i.test(window.navigator.userAgent);
}

/**
 * True only on real phones/tablets.
 * Chrome DevTools "device mode" spoofs the UA but usually leaves
 * navigator.platform as Win32 / MacIntel / Linux — so Inspect should not show the popup.
 */
export function isRealMobileDevice() {
  if (typeof window === 'undefined') return false;

  const ua = window.navigator.userAgent || '';
  const platform = window.navigator.platform || '';
  const maxTouch = window.navigator.maxTouchPoints || 0;

  const uaAndroid = /Android/i.test(ua);
  const uaIphone = /iPhone|iPod/i.test(ua);
  const uaIpad = /iPad/i.test(ua) || (/Macintosh/i.test(ua) && maxTouch > 1);

  if (!uaAndroid && !uaIphone && !uaIpad) return false;

  // Desktop OS platform while UA claims mobile → almost always DevTools emulation
  const desktopPlatform =
    /Win32|Win64|Windows|MacIntel|Linux x86_64|Linux aarch64|CrOS/i.test(platform) &&
    !/iPhone|iPad|iPod/i.test(platform);

  if (desktopPlatform) {
    // Real iPadOS can report MacIntel + touch; allow that only.
    if (/MacIntel/i.test(platform) && maxTouch > 1 && uaIpad) return true;
    return false;
  }

  return true;
}

export function getMobileOsLabel() {
  if (isIosDevice()) return 'iOS';
  if (isAndroidDevice()) return 'Android';
  return 'mobile';
}
